import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './GiantsSupplyToolbar.css'
import ModalButton from "../../../../Modals/ModalWindow/ModalButton/ModalButton";
import {giantsConvertSupply, showToast} from "../../../../../redux/actions";
import {
  selectGiantsSelectedUnitId,
  selectIsAgrarianismConversionOn,
  selectPendingSupply,
  selectUnits
} from "../../../../../redux/reducers/sharedStateReducer/selectors";
import {GIANTS_ADDITIONAL_SUPPLY_COST} from "../../../../../configs/factions/giants";
import {REGISTRY_SINGLETON} from "../../../../../scenes/Registry";
import DescriptionValueWithIcon from "../../../../PolicyCardOverlay/DescriptionValueWithIcon/DescriptionValueWithIcon";
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer
} from "../../../../../utils/gameState/supply/parallelSupplySelectors";

// Показывается только для выбранного (кликом по нему на поле) великана - счётчик
// уже полученного за ход базового снабжения и кнопка конвертации 4 базовых <->
// 1 дополнительное. Раздача снабжения кликами по фермам/отрядам работает как
// обычно и от этого выбора не зависит
const GiantsSupplyToolbar = ({withAgrarianism}) => {
  const dispatch = useDispatch();
  // Выбор великана и обмен по аграризму — того игрока, за которого клиент
  // раздает снабжение: в одновременной фазе снабжения сетевой игры у каждого
  // игрока свой выбор (см. selectSupplyActingPlayer), в поочередной — текущего
  const supplyPlayer = useSelector(selectSupplyActingPlayer);
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  const selectedUnitId = useSelector((state) => selectGiantsSelectedUnitId(
    state, selectSupplyActingPlayer(state)?.id ?? null
  ));
  const units = useSelector(selectUnits);
  const pendingSupply = useSelector(selectPendingSupply);
  const isAgrarianismConversionOn = useSelector((state) => selectIsAgrarianismConversionOn(
    state, selectSupplyActingPlayer(state)?.id ?? null
  ));

  // Обмен снабжения на монеты (кнопка "Обменять" запускается не кликом по полю,
  // а самой кнопкой - выбор великана этим не сбрасывается, поэтому скрываем
  // тулбар отдельно, чтобы не показывать лишнюю информацию поверх обмена
  if (!selectedUnitId || isAgrarianismConversionOn) {
    return null;
  }

  const unitEntries = pendingSupply.filter(s => s.unitId === selectedUnitId);
  const hasAdditionalSupply = unitEntries.some(s => s.additionalSupply);
  // Для конвертации важно только количество ОТДЕЛЬНЫХ базовых записей (доп.
  // снабжение расходует ровно 4 из них за раз)
  const basicSupplyCount = unitEntries.filter(s => !s.additionalSupply).length;
  // А для отображения "получено снабжение с N ферм" считаем ВСЕ фермы, включая
  // те 4, что сейчас превращены в дополнительное снабжение (batched-запись
  // хранит их в farmHexKeys) - это число не должно меняться от конвертации
  // туда и обратно
  const totalFarmsCount = unitEntries.reduce((count, s) => {
    if (s.additionalSupply) {
      return count + (s.farmHexKeys?.length || 0);
    }
    return count + 1;
  }, 0);

  const handleConvert = () => {
    if (!supplyPlayer) {
      return;
    }
    const unit = units.find(u => u.id === selectedUnitId);
    if (!unit) {
      dispatch(showToast('Отряд не найден.', 'error', 3000));
      return;
    }

    if (!hasAdditionalSupply && basicSupplyCount < GIANTS_ADDITIONAL_SUPPLY_COST + 1) {
      dispatch(showToast(
        `Не хватает ${GIANTS_ADDITIONAL_SUPPLY_COST + 1 - basicSupplyCount} снабжения, чтобы дать дополнительное:\nВеликану нужно минимум ${GIANTS_ADDITIONAL_SUPPLY_COST + 1} `
        + `(сейчас ${basicSupplyCount}) — хотя бы 1 должно остаться отряду как базовое.`,
        'warning', 5000
      ));
      return;
    }

    // В одновременной фазе — за своего игрока явно, в поочередной — без
    // playerId, за текущего игрока, как всегда
    dispatch(giantsConvertSupply(selectedUnitId, isParallelSupplyPhase ? supplyPlayer.id : undefined));
    REGISTRY_SINGLETON.getSoundManager().playDistributeSupply();
    dispatch(showToast(
      hasAdditionalSupply
        ? 'Дополнительное снабжение превращено обратно в 4 базовых.'
        : `${GIANTS_ADDITIONAL_SUPPLY_COST} базовых снабжения превращены в дополнительное.`,
      'success', 3000
    ));
  }


  const supply = <img
    className={'policy-description-icon'}
    src={'/img/icons/supply.png'}
    alt={'Снабжение'}
    title={'Снабжение'}
  />;
  const additionalSupplyCostValueAndIcon = <DescriptionValueWithIcon
    className={'text-supply policy-description-icon-margin-right-left'}
    value={GIANTS_ADDITIONAL_SUPPLY_COST}
    icon={supply}
  />;
  const totalSupplyValueAndIcon = <DescriptionValueWithIcon
    className={'text-supply policy-description-icon-margin-right-left'}
    value={totalFarmsCount}
    icon={supply}
  />;
  const baseSupplyValueAndIcon = <DescriptionValueWithIcon
    className={'text-supply policy-description-icon-margin-right-left'}
    value={totalFarmsCount - GIANTS_ADDITIONAL_SUPPLY_COST}
    icon={supply}
  />;

  return (
    <div className={'giants-supply-toolbar'} style={{marginBottom: withAgrarianism ? 10 : 0}}>
      <div className={'giants-supply-toolbar_hint'}>
        <div className="policy-description_line">
          Получено {totalSupplyValueAndIcon}
        </div>
        {hasAdditionalSupply && (
          <>
            <div className="policy-description_line">
              • Из них {additionalSupplyCostValueAndIcon} на доп.снабжение
            </div>
            <div className="policy-description_line">
              • Остальные {baseSupplyValueAndIcon} на рост размера
            </div>
          </>
        )}
      </div>
      <ModalButton
        onClick={handleConvert}
        className={'giants-supply-toolbar_button'}
        noSound={true}
        title={'Превратить 4 полученных базовых снабжения в дополнительное, и наоборот'}
      >
        <h2>{hasAdditionalSupply ? 'Убрать доп.' : 'Дать доп.'}</h2>
        <img
          className={'giants-supply-toolbar_icon'}
          src={'/img/icons/supply outline.png'}
          alt={'Снабжение'}
          title={'Снабжение'}
        />
      </ModalButton>
    </div>
  );
};

export default GiantsSupplyToolbar;
